import {
  Box,
  CircularProgress,
  Container,
  List,
  ListItemAvatar,
  ListItemText,
  Typography,
  useTheme,
  ListItemButton,
  Stack,
  Chip,
  FormControl,
  OutlinedInput,
  InputAdornment,
} from "@mui/material"
import React, { useState, useCallback, useEffect, useContext } from "react"

import type { IWorld } from "../../interfaces/world.interface"
import { worldService } from "../../services"
import SettingsContext from "../../contexts/settings.context"
import { HiGlobeAmericas, HiMagnifyingGlass } from "react-icons/hi2"
import { UPDATE_SETTINGS } from "../../contexts/reducers/settings.reducer"
import { hexToRGB } from "../../components/FormBuilder/utilities"
import LayoutContext from "../../contexts/layout.context"
import { SheetTopBar } from "../../components"
import SelectProjectList from "../Projects/SelectProjectList.page"
import { useNavigate } from "react-router-dom"

function SelectWordList() {
  const theme = useTheme()
  const [worlds, setWorlds] = useState<IWorld[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { settingsState, updateSettings, resetFilters } = useContext(SettingsContext)
  const { closeSidebar, openSidebar } = useContext(LayoutContext)
  const [searchText, setSearchText] = useState<string>("")
  const navigate = useNavigate()

  const fetch = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await worldService.getWorlds()
      if (res) {
        setWorlds(res)
        setIsLoading(false)
      }
    } catch (error) {
      console.log(error)
    }
  }, [])

  useEffect(() => {
    fetch()
  }, [])

  const handleOnSelect = (world: IWorld) => {
    updateSettings(UPDATE_SETTINGS, {
      ...settingsState,
      activeWorld: world,
      activeSurvey: undefined,
      activeProject: undefined,
      activeFeature: undefined,
      sidebar: null,
    })
    resetFilters()

    navigate("/")

    openSidebar(<SelectProjectList />)
  }

  const renderSearchBox = () => {
    return (
      <Stack flexDirection={"row"} py={2} alignItems={"center"} gap={1}>
        <Box
          width={"100%"}
          display={"flex"}
          gap={1}
          border={`1px solid ${theme.palette.divider}`}
          bgcolor={theme.palette.background.paper}
          p={0.2}
          height={44}
          borderRadius={3}
          mt={-2.2}
          mb={-3.2}>
          <FormControl
            fullWidth
            size="small"
            variant="outlined"
            sx={{
              "& fieldset": {
                border: "none !important",
              },
            }}>
            <OutlinedInput
              id="outlined-adornment-password"
              type={"text"}
              //sx={{ bgcolor: theme.palette.background.default }}
              placeholder="Search..."
              onChange={(event) => setSearchText(event.target.value)}
              value={searchText}
              startAdornment={
                <InputAdornment position="start">
                  <HiMagnifyingGlass />
                </InputAdornment>
              }
            />
          </FormControl>
        </Box>
      </Stack>
    )
  }

  const filteredWorlds = worlds
    .filter((world) => world.worldName?.toLowerCase().includes(searchText.toLowerCase()))
    .sort((a, b) => (a.worldName || "").localeCompare(b.worldName || ""))

  const renderLoadingContainer = () => (
    <Box width={"100%"} display={"flex"} justifyContent={"center"} py={12} alignItems={"center"}>
      <CircularProgress />
    </Box>
  )

  return (
    <>
      <SheetTopBar
        onClose={() => closeSidebar()}
        avatar={<HiGlobeAmericas color="white" size={36} opacity={0.7} />}
        leading={
          <Stack gap={0}>
            <Typography color={"white"} fontWeight={900}>
              Worlds
            </Typography>
          </Stack>
        }
      />
      <Container maxWidth={false} sx={{ pt: 2, marginBottom: 7 }}>
        {isLoading ? (
          renderLoadingContainer()
        ) : (
          <>
            {renderSearchBox()}
            <Box
              sx={{
                my: 2,
                background: theme.palette.background.paper,
                borderRadius: 3,
                borderWidth: 1,
                borderStyle: "solid",
                borderColor: theme.palette.grey[300],
                overflow: "hidden",
              }}>
              <List sx={{ paddingY: 1, marginBottom: -1 }} dense>
                {filteredWorlds &&
                  filteredWorlds.map((world, index) => {
                    return (
                      <ListItemButton
                        dense
                        sx={{ cursor: "pointer" }}
                        key={index}
                        onClick={() => handleOnSelect(world)}>
                        <ListItemAvatar sx={{ display: "flex", justifyContent: "start", alignItems: "center" }}>
                          <HiGlobeAmericas color={theme.palette.primary.main} size={32} />
                        </ListItemAvatar>

                        <ListItemText
                          primaryTypographyProps={{ fontWeight: "bold" }}
                          secondary={"World"}
                          secondaryTypographyProps={{ fontSize: 12 }}
                          primary={world.worldName}
                        />
                        {settingsState.activeWorld && settingsState.activeWorld.worldId === world.worldId && (
                          <Chip
                            size="small"
                            sx={{
                              backgroundColor: hexToRGB(theme.palette.success.main, 0.1),
                              color: theme.palette.success.main,
                            }}
                            label="Active"
                          />
                        )}
                      </ListItemButton>
                    )
                  })}
              </List>
            </Box>
          </>
        )}
      </Container>
    </>
  )
}

export default SelectWordList
